import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getdata, updatedata } from "../../util/global";
import { subscription } from './Datatype';

const frequencytexts: Record<string, string> = { weekly: 'every week', biweekly: 'every 2 weeks' };
const durationtexts: Record<string, string> = { '1month': '1 month', '3months': '3 months', '1year': '1 year' };
// list order: active first, then cancelling, then pending, every other status after those
const statusorder: Record<string, number> = { active: 0, cancelling: 1, pending: 2 };

const muted: React.CSSProperties = { fontSize: '0.8rem', color: 'var(--muted)' };
const row: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center' };

// iso date string to a short readable date, e.g. "5 Nov 2026"
const formatdate = (iso: string) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const MySubscriptions = () => {
  const navigate = useNavigate();
  const [subscriptions, setsubscriptions] = useState<subscription[]>([]);
  const [loaded, setloaded] = useState(false);
  const [seq, setseq] = useState<Record<string, number>>({});
  // cancelling holds the subscription whose cancel confirmation is open, null when it is closed
  const [cancelling, setcancelling] = useState<subscription | null>(null);
  const [busy, setbusy] = useState(false);
  const [cancelerror, setcancelerror] = useState('');

  const fetchsubscriptions = async () => {
    const res = await getdata({ filter: { datatype: "subscriptiondata" } });
    const map: Record<string, number> = {};
    [...res]
      .sort((a: subscription, b: subscription) => a._id!.localeCompare(b._id!))
      .forEach((s: subscription, i: number) => { map[s._id!] = i + 1; });
    setseq(map);
    res.sort((a: subscription, b: subscription) => (statusorder[a.status] ?? 3) - (statusorder[b.status] ?? 3));
    setsubscriptions(res);
    setloaded(true);
  };

  // new record from the existing one, _id dropped so the api inserts; the response carries the new id
  const copy = async (sub: subscription) => {
    const { _id, ...rest } = sub;
    const res = await updatedata({ data: { ...rest, label: 'Copy of ' + sub.label, status: 'pending' } });
    navigate('/subscribe?id=' + res[0].id);
  };

  // asks the api to cancel; on success the list is reloaded and the confirmation closes, otherwise the error stays in it
  const confirmcancel = async () => {
    setbusy(true);
    setcancelerror('');
    const res = await getdata({ id: cancelling!._id }, '/cancelsubscription');
    if (res && res.status === 'success') {
      await fetchsubscriptions();
      setcancelling(null);
    } else {
      setcancelerror(res?.error || 'Could not cancel. Please try again.');
    }
    setbusy(false);
  };

  useEffect(() => {
    fetchsubscriptions();
  }, []);

  if (!loaded) return null;

  if (subscriptions.length === 0) {
    return (
      <div className="content-area" style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
        <div className="eyebrow">MY SUBSCRIPTIONS</div>
        <h2 style={{ fontFamily: 'var(--display)', fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>No deliveries yet.</h2>
        <div className="sub-count">Set up your first subscription. Pick your bottles, choose a delivery day, add your address.</div>
        <Link to="/shop" className="btn-blue">Add new subscription</Link>
      </div>
    );
  }

  return (
    <div className="content-area" style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      <div style={{ ...row, alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <div className="eyebrow">MY SUBSCRIPTIONS</div>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.03em', margin: 0 }}>Your deliveries.</h2>
          <div className="sub-count">{subscriptions.length} subscription{subscriptions.length === 1 ? '' : 's'}. Add one per address, copy an existing one to start faster.</div>
        </div>
        <Link to="/shop" className="btn-blue">Add new subscription</Link>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        {subscriptions.map((sub) => (
          <div key={sub._id} className="c-form">
            <div style={{ ...row, marginBottom: '10px' }}>
              <span><span className="sub-index">#{seq[sub._id!]}</span><strong>{sub.label}</strong></span>
              <span className={`status-badge ${sub.status}`}>{sub.status}</span>
            </div>
            <div style={muted}>{sub.address}, {sub.pin}</div>
            <div style={{ ...muted, marginBottom: '14px' }}>{sub.name} · {sub.phone}</div>

            {sub.items.map((item) => (
              <div key={item.productid}>{item.quantity} x {item.name}</div>
            ))}

            <div style={{ ...muted, margin: '10px 0 16px' }}>
              {frequencytexts[sub.frequency]} · preferably {sub.preferredday} · {durationtexts[sub.duration]} plan
            </div>
            {/* a cancelling subscription keeps running until the end of the paid period */}
            {sub.status === 'cancelling' && <div style={{ ...muted, margin: '-10px 0 16px' }}>Cancellation raised. Deliveries continue till {formatdate(sub.enddate)}.</div>}

            <div style={{ display: 'flex', gap: '8px' }}>
              {sub.status === 'pending' && (
                <button type="button" className="btn-small" onClick={() => navigate('/checkout/' + sub._id)}>Checkout</button>
              )}
              {/* a cancelling or cancelled subscription cannot be edited, only copied to start a fresh one */}
              {sub.status !== 'cancelling' && sub.status !== 'cancelled' && (
                <button type="button" className="btn-small" onClick={() => navigate('/subscribe?id=' + sub._id)}>Edit</button>
              )}
              <button type="button" className="btn-small" onClick={() => copy(sub)}>Copy</button>
              {/* only an active subscription can be cancelled */}
              {sub.status === 'active' && (
                <button type="button" className="btn-small btn-ghost" onClick={() => setcancelling(sub)}>Cancel subscription</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* cancel confirmation, shown over the page while a subscription is picked for cancelling */}
      {cancelling && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="c-form" style={{ maxWidth: '420px', width: '90%' }}>
            <div style={{ marginBottom: '10px' }}><strong>Cancel {cancelling.label}?</strong></div>
            <div style={{ marginBottom: '16px' }}>
              Your current subscription window is till {formatdate(cancelling.enddate)}. This subscription will end after that. No further payments will be collected.
            </div>
            {cancelerror && <div style={{ ...muted, color: '#B42318', marginBottom: '16px' }}>{cancelerror}</div>}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button type="button" className="btn-small" onClick={() => { setcancelling(null); setcancelerror(''); }}>Keep subscription</button>
              <button type="button" className="btn-blue" onClick={confirmcancel} disabled={busy}>{busy ? 'Cancelling...' : 'Cancel subscription'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscriptions;
